import { useTranslation } from 'react-i18next';

/**
 * Custom hook for handling translations with namespace support
 * @param namespace - Optional namespace for translations
 * @returns Translation utilities
 */
export const useTranslations = (namespace?: string) => {
  const { t, i18n } = useTranslation(namespace);

  return {
    /**
     * Translate a key with optional interpolation
     * @param key - Translation key
     * @param defaultValue - Default fallback value if key is not found
     * @param options - Optional interpolation values
     */
    t: (key: string, defaultValue?: string, options?: Record<string, unknown>) => {
      return t(key, defaultValue ? { defaultValue, ...options } : options);
    },

    /**
     * Get the current language
     */
    currentLanguage: i18n.language,

    /**
     * Change the current language
     * @param lang - Language code to change to
     */
    changeLanguage: (lang: string) => i18n.changeLanguage(lang),

    /**
     * Check if the current language is RTL
     */
    isRTL: ['ar', 'he', 'fa', 'ur'].includes(i18n.language),

    /**
     * Get all available languages
     */
    languages: {
      en: 'English',
      es: 'Spanish',
    },

    /**
     * i18n instance for advanced usage
     */
    i18n,
  };
};

export default useTranslations;
